import {
  DashboardView,
  DASHBOARD_VIEW_TYPE,
} from '@adtrackify/at-tracking-event-types';
import { QueryClient, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import * as Logger from '@/lib/monitoring/logger';
import { dashboardKeys } from '@/features/dashboard/api/dashboard-keys';
import { isConfiguredDashboardView } from '@/features/dashboard/helpers/view-helpers';
import { useDashboardLayoutStore } from '@/features/dashboard/stores/dashboard-layout-store';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import type { DashboardBootstrapResponse } from '@/features/dashboard/types/overview-types';

export function hydrateBootstrapCaches(
  queryClient: QueryClient,
  pixelId: string,
  data: DashboardBootstrapResponse,
  options: Pick<UseDashboardBootstrapOptions, 'type'> = {}
): void {
  queryClient.setQueryData(dashboardKeys.pages.list(pixelId), data.pages);

  const viewsByType = data.viewsByType ?? ({} as DashboardBootstrapResponse['viewsByType']);
  const typesToHydrate = options.type
    ? [options.type]
    : (Object.keys(viewsByType) as DASHBOARD_VIEW_TYPE[]);

  for (const type of typesToHydrate) {
    const list = viewsByType[type];
    if (!list) continue;
    queryClient.setQueryData(dashboardKeys.views.list(pixelId, type), list);
    hydrateViewDetailCaches(queryClient, list);
  }

  const currentByType =
    data.currentViewsByType ?? ({} as DashboardBootstrapResponse['currentViewsByType']);
  for (const type of Object.keys(currentByType) as DASHBOARD_VIEW_TYPE[]) {
    const currentView = currentByType[type];
    if (!currentView) continue;
    hydrateViewDetailCaches(queryClient, [currentView]);
  }
}

function hydrateViewDetailCaches(queryClient: QueryClient, views: DashboardView[]): void {
  for (const view of views) {
    const detailKey = dashboardKeys.view(view.id);
    const cachedView = queryClient.getQueryData<DashboardView | undefined>(detailKey);
    if (shouldHydrateDetailView(cachedView, view)) {
      queryClient.setQueryData(detailKey, view);
    }
  }
}

function shouldHydrateDetailView(
  cachedView: DashboardView | undefined,
  bootstrapView: DashboardView
): boolean {
  if (!isConfiguredDashboardView(bootstrapView)) {
    return false;
  }
  if (!isConfiguredDashboardView(cachedView)) {
    return true;
  }
  if (cachedView.id !== bootstrapView.id) return true;

  const cachedUpdatedAt = Date.parse(cachedView.updatedAt ?? '');
  const bootstrapUpdatedAt = Date.parse(bootstrapView.updatedAt ?? '');

  if (Number.isFinite(cachedUpdatedAt) && !Number.isFinite(bootstrapUpdatedAt)) {
    return false;
  }

  if (Number.isFinite(cachedUpdatedAt) && Number.isFinite(bootstrapUpdatedAt)) {
    return bootstrapUpdatedAt >= cachedUpdatedAt;
  }

  return false;
}

export const dashboardBootstrapKeys = {
  all: ['dashboard', 'bootstrap'] as const,
  byPixel: (pixelId: string, _viewId?: string | null) =>
    [...dashboardBootstrapKeys.all, pixelId] as const,
  byPixelAndType: (pixelId: string, type: DASHBOARD_VIEW_TYPE, _viewId?: string | null) =>
    [...dashboardBootstrapKeys.all, pixelId, type] as const,
};

interface UseDashboardBootstrapOptions {
  onSuccess?: (data: DashboardBootstrapResponse) => void;
  enabled?: boolean;
  type?: DASHBOARD_VIEW_TYPE;
}

export function useDashboardBootstrap(
  pixelId: string | null | undefined,
  viewId?: string | null,
  options?: UseDashboardBootstrapOptions
) {
  const queryClient = useQueryClient();
  const filterType = options?.type;
  const fetchStartTime = useRef<number>(0);
  const isEnabled = options?.enabled !== undefined ? options.enabled && !!pixelId : !!pixelId;

  const queryKey = filterType
    ? dashboardBootstrapKeys.byPixelAndType(pixelId ?? '', filterType, viewId)
    : dashboardBootstrapKeys.byPixel(pixelId ?? '', viewId);

  const query = useQuery<DashboardBootstrapResponse, Error>({
    queryKey,
    queryFn: async (): Promise<DashboardBootstrapResponse> => {
      if (!pixelId) {
        throw new Error('pixelId is required to fetch dashboard bootstrap');
      }
      fetchStartTime.current = performance.now();
      Logger.debug('useDashboardBootstrap fetching', {
        pixelId,
        viewId,
        filterType,
      });
      const response = await AccountsApiService.getDashboardBootstrap(
        pixelId,
        viewId ?? undefined,
        filterType
      );
      return response.data;
    },
    enabled: isEnabled,
    useErrorBoundary: false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    // Refetch on tab focus / dashboard remount so external edits to the
    // initial view (read from bootstrap.currentViewsByType until the user
    // explicitly switches) are picked up. Skip while a layout drag is
    // unsaved — the autosave subscription will fire shortly and we don't
    // want to overwrite in-flight edits with the pre-drag server state.
    refetchOnMount: () =>
      !useDashboardLayoutStore.getState().hasUnsavedLayoutChanges,
    refetchOnWindowFocus: () =>
      !useDashboardLayoutStore.getState().hasUnsavedLayoutChanges,
    onSuccess: (data: DashboardBootstrapResponse) => {
      const durationMs = Math.round(performance.now() - fetchStartTime.current);
      const viewsByType = data.viewsByType ?? ({} as DashboardBootstrapResponse['viewsByType']);
      const currentByType =
        data.currentViewsByType ?? ({} as DashboardBootstrapResponse['currentViewsByType']);
      Logger.debug('useDashboardBootstrap success', {
        pixelId,
        viewId,
        filterType,
        durationMs,
        pageCount: data.pages?.length ?? 0,
        typeCounts: Object.fromEntries(
          (Object.keys(viewsByType) as DASHBOARD_VIEW_TYPE[]).map(t => [
            t,
            viewsByType[t]?.length ?? 0,
          ])
        ),
        currentViewIds: Object.fromEntries(
          (Object.keys(currentByType) as DASHBOARD_VIEW_TYPE[]).map(t => [
            t,
            currentByType[t]?.id,
          ])
        ),
      });

      if (pixelId) {
        hydrateBootstrapCaches(queryClient, pixelId, data, { type: filterType });
      }
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      const durationMs = Math.round(performance.now() - fetchStartTime.current);
      Logger.warn('useDashboardBootstrap error', {
        pixelId,
        viewId,
        filterType,
        durationMs,
        error,
      });
    },
  } as UseQueryOptions<DashboardBootstrapResponse, Error>);

  useEffect(() => {
    Logger.debug('useDashboardBootstrap mounted', {
      pixelId,
      viewId,
      filterType,
      isEnabled,
    });
  }, []);

  useEffect(() => {
    Logger.debug('useDashboardBootstrap state', {
      pixelId,
      viewId,
      filterType,
      isEnabled,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isSuccess: query.isSuccess,
      isError: query.isError,
      status: query.status,
      fetchStatus: query.fetchStatus,
    });
  }, [
    pixelId,
    viewId,
    filterType,
    isEnabled,
    query.isLoading,
    query.isFetching,
    query.isSuccess,
    query.isError,
    query.status,
    query.fetchStatus,
  ]);

  return query;
}
